import { useEffect, useState } from "react";
import api, { apiError } from "../../../lib/api";
import { toast } from "sonner";

export function Settings() {
  const [record, setRecord] = useState(null);
  const [resets, setResets] = useState([]);
  const [wire, setWire] = useState("");
  const [busy, setBusy] = useState(false);
  const [schedule, setSchedule] = useState(null);
  const [routing, setRouting] = useState(null);

  useEffect(() => {
    api.get("/public/record").then((r) => setRecord(r.data)).catch(() => {});
    api.get("/admin/reset-requests").then((r) => setResets(r.data)).catch(() => {});
    api.get("/settlement/instructions").then((r) => setWire(r.data.text)).catch(() => {});
    api.get("/admin/statement-schedule").then((r) => setSchedule(r.data)).catch(() => {});
    api.get("/admin/enquiry-routing").then((r) => setRouting(r.data)).catch(() => {});
  }, []);

  const saveWire = async () => {
    try {
      await api.put("/admin/settlement-instructions", { text: wire });
      toast.success("Wire instructions updated. Shown to allocated members at settlement.");
    } catch (e) { toast.error(apiError(e)); }
  };

  const issueStatements = async () => {
    try {
      const { data } = await api.post("/admin/statements/issue-all");
      toast.success(data.message || "Statements are being issued.");
    } catch (e) { toast.error(apiError(e)); }
  };

  const saveSchedule = async () => {
    try {
      const { data } = await api.put("/admin/statement-schedule", schedule);
      setSchedule(data);
      toast.success("Statement schedule saved.");
    } catch (e) { toast.error(apiError(e)); }
  };

  const saveRouting = async () => {
    try {
      const { data } = await api.put("/admin/enquiry-routing", routing);
      setRouting(data);
      toast.success("Enquiry routing saved.");
    } catch (e) { toast.error(apiError(e)); }
  };

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/admin/record", {
        transactions: Number(record.transactions), median_days: Number(record.median_days), as_at: record.as_at,
      });
      setRecord(data);
      toast.success("Record line updated.");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const copyLink = (token) => {
    navigator.clipboard.writeText(`${window.location.origin}/reset-password?token=${token}`);
    toast.success("Reset link copied. Share it with the member directly.");
  };

  if (!record) return <span className="t-label">Loading</span>;

  return (
    <div className="grid md:grid-cols-2 gap-12" data-testid="admin-settings">
      <div className="space-y-3 max-w-md">
        <span className="t-label">Record line, shown on the landing page</span>
        <p className="t-cap text-muted-s">The transactions figure increments automatically when a transfer is completed.</p>
        <input className="input-solera fig" type="number" value={record.transactions}
          onChange={(e) => setRecord({ ...record, transactions: e.target.value })} data-testid="record-transactions-input" />
        <input className="input-solera fig" type="number" value={record.median_days}
          onChange={(e) => setRecord({ ...record, median_days: e.target.value })} data-testid="record-median-input" />
        <input className="input-solera" value={record.as_at} placeholder="As at, e.g. 1 June 2026"
          onChange={(e) => setRecord({ ...record, as_at: e.target.value })} data-testid="record-asat-input" />
        <button className="btn-primary" disabled={busy} onClick={save} data-testid="record-save-button">Save record</button>
        <div className="hairline mt-8 pt-6 space-y-3">
          <span className="t-label">Wire instructions, shown to allocated members</span>
          <textarea className="input-solera fig" rows={7} value={wire}
            onChange={(e) => setWire(e.target.value)} data-testid="wire-instructions-input" />
          <button className="btn-primary" onClick={saveWire} data-testid="wire-save-button">Save wire instructions</button>
        </div>
        <div className="hairline mt-8 pt-6 space-y-4">
          <span className="t-label">Quarterly statements</span>
          <p className="t-cap text-muted-s">
            Statements are issued automatically at 06:00 UTC on the day and quarter months you choose. You can also issue to everyone right now.
          </p>
          {schedule && (
            <div className="space-y-4" data-testid="statement-schedule">
              <label className="flex items-center gap-3 cursor-pointer">
                <input type="checkbox" checked={schedule.enabled} onChange={(e) => setSchedule({ ...schedule, enabled: e.target.checked })} className="w-4 h-4" data-testid="schedule-enabled-toggle" />
                <span className="t-small text-bone">Automatic quarterly issuance enabled</span>
              </label>
              <div className="flex items-center gap-3">
                <span className="t-cap text-muted-s">Day of month</span>
                <input type="number" min="1" max="28" value={schedule.day} onChange={(e) => setSchedule({ ...schedule, day: Number(e.target.value) })} className="input-solera fig w-24" data-testid="schedule-day-input" />
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                {[[1, "Jan"], [4, "Apr"], [7, "Jul"], [10, "Oct"]].map(([mm, lbl]) => {
                  const on = schedule.months.includes(mm);
                  return (
                    <button key={mm} type="button" onClick={() => setSchedule({ ...schedule, months: on ? schedule.months.filter((x) => x !== mm) : [...schedule.months, mm] })}
                      className={`px-4 py-2 t-label ${on ? "bg-[#E8E6E1] !text-[#0E1012]" : "border border-[#24272B]"}`} data-testid={`schedule-month-${mm}`}>
                      {lbl}
                    </button>
                  );
                })}
              </div>
              <button className="btn-primary" onClick={saveSchedule} data-testid="save-schedule-button">Save schedule</button>
            </div>
          )}
          <div className="hairline pt-4">
            <button className="btn-ghost" onClick={issueStatements} data-testid="issue-statements-button">Issue statements to all members now</button>
          </div>
        </div>
        <div className="hairline mt-8 pt-6 space-y-3" data-testid="enquiry-routing">
          <span className="t-label">Enquiry routing</span>
          <p className="t-cap text-muted-s">
            Route new contact enquiries to a teammate based on the auto detected category. Leave a field blank to
            send only to the shared inbox. Default catches anything without a category match.
          </p>
          {routing && ["broker", "allocator", "shareholder", "other", "default"].map((k) => (
            <div key={k} className="flex items-center gap-3">
              <span className="t-cap text-muted-s w-24 capitalize">{k === "other" ? "General" : k}</span>
              <input className="input-solera" type="email" placeholder="teammate@solera.vc" value={routing[k] || ""}
                onChange={(e) => setRouting({ ...routing, [k]: e.target.value })} data-testid={`routing-${k}-input`} />
            </div>
          ))}
          {routing && (
            <div className="flex items-center gap-3">
              <span className="t-cap text-muted-s w-24">Slack</span>
              <input className="input-solera" placeholder="https://hooks.slack.com/services/..." value={routing.slack_webhook || ""}
                onChange={(e) => setRouting({ ...routing, slack_webhook: e.target.value })} data-testid="routing-slack-input" />
            </div>
          )}
          <button className="btn-primary" onClick={saveRouting} data-testid="save-routing-button">Save routing</button>
        </div>
      </div>
      <div>
        <span className="t-label">Pending password reset requests</span>
        <p className="t-cap text-muted-s mt-2 measure">
          No reset emails are sent. Verify the member directly, then share the link with them.
          Links expire after 30 minutes.
        </p>
        <div className="mt-4 space-y-0" data-testid="reset-requests-list">
          {resets.map((r) => (
            <div key={r.token} className="hairline py-4 flex justify-between gap-6 items-baseline">
              <div>
                <span className="t-small text-bone">{r.email}</span>
                <span className="t-cap text-muted-s ml-3 fig">{r.created_at?.slice(0, 16).replace("T", " ")} UTC</span>
              </div>
              <button onClick={() => copyLink(r.token)} className="t-label hover:!text-[#E8E6E1]"
                data-testid={`copy-reset-link-${r.token}`}>Copy link</button>
            </div>
          ))}
          {resets.length === 0 && <p className="t-small text-muted-s hairline py-4">None pending.</p>}
        </div>
      </div>
    </div>
  );
}
